'use client';

import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { HeaderCartButton } from './_components/header-cart-button.component';
import { HeaderOpenPaymentsButton } from './_components/header-open-payments-button.component';
import { HeaderNavbarItem } from './_components/header-navbar-item.component';
import { NeedHelpButton } from './_components/header-need-help-button.component';
import { NavbarProfile } from './_components/header-navbar-profile.component';
import { NavbarSheet } from './_components/header-navbar-sheet.component';
import { navItems } from './_consts/header-nav-items.const';
import { useHeaderAutoHide } from './_hooks/use-header-auto-hide.hook';

import logoSvg from '@/assets/medprev/logo.svg?url';
import { ContentContainer } from '@/components/ui/content-container.component';
import { Link } from '@/components/ui/link/link.component';
import { useNavigation } from '@/hooks/navigation/use-navigation.hook';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { headerStore } from '@/store/header.store';
import { cn } from '@/utils/cn-tw-merge';

const Header = () => {
  const [mounted, setMounted] = useState(false);
  const isHidden = useStore(headerStore, (state) => state.isScrolled);
  const { getHomeLink } = useNavigation();
  const pathname = usePathname();
  const isSearchPage = pathname?.startsWith('/busca/') ?? false;

  useHeaderAutoHide(!isSearchPage);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <header className="h-16 md:h-14 border-b border-b-gray-200 bg-white" />;
  }

  return (
    <header
      data-aria-hidden={isHidden}
      aria-hidden={isHidden}
      className={cn(
        'sticky top-0 z-50 bg-white transition-[transform,opacity] duration-300 ease-in-out',
        isHidden ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100',
      )}
    >
      <div className="border-b border-gray-300 h-16 md:h-14">
        <ContentContainer className="flex justify-between items-center h-full">
          <div className="flex items-center gap-4 md:gap-8 h-full">
            <NavbarSheet navItems={navItems} />
            <Link
              prefetch={false}
              href={getHomeLink()}
              data-testid={DataTestIdEnum.HEADER_LINK_LOGO}
              data-analytics-event={AnalyticsEnum.CLICK_HEADER_NAV_ITEM}
              data-analytics-properties={JSON.stringify({ item_header: 'logo medprev' })}
            >
              <Image src={logoSvg} className="h-7 w-auto" alt="logo" data-testid="logo" priority />
            </Link>
            <nav className="hidden lg:flex h-full" data-testid="navbar">
              {navItems.map((item) => (
                <HeaderNavbarItem key={item.id} {...item} />
              ))}
            </nav>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <HeaderOpenPaymentsButton />
            <NeedHelpButton />
            <HeaderCartButton />
            <NavbarProfile />
          </div>
        </ContentContainer>
      </div>
      <div id="header-location-slot" />
    </header>
  );
};

export default Header;
